/* eslint-disable */
import { createSlice } from '@reduxjs/toolkit';
import axios from '../../utils/axios';
import { dispatch } from '../index';
import { handleError } from '../../utils/apiResponseHandler.js';
import { StorageManager } from '../../utils/storageManager.js';

const initialState = {
    error: null,
    loading: false,
    statsLoading: false,

    // Overview statistics
    stats: {
        totalCalls: 0,
        inboundCalls: 0,
        outboundCalls: 0,
        totalDuration: 0,
        averageDuration: 0,
        callOutcomes: {
            completed: 0,
            failed: 0,
            busy: 0,
            noAnswer: 0,
        },
        sentimentAnalytics: {
            total: 0,
            positive: 0,
            negative: 0,
            neutral: 0,
        },
        commonConcerns: {},
    },

    // All call history data
    allCallHistory: null,
    totalPages: null,
    totalCount: null,
    resSummary: null,

    // Single call data
    singleCallData: null,
    singleCallDataLoading: false,

    // Stats filters (for charts and statistics)
    statsFilters: {
        start_date: { value: "", disabled: false, type: "date" },
        end_date: { value: "", disabled: false, type: "date" },
    },

    // Table filters (for call history table)
    filters: {
        search: { value: "", disabled: false, type: "string" },
        call_type: { value: "inbound", disabled: false, type: "string" }, // default to inbound
        start_date: { value: "", disabled: false, type: "date" },
        end_date: { value: "", disabled: false, type: "date" },
        call_order: { value: "desc", disabled: false, type: "string" },
    },

    currentPage: 1,
    totalResPerPage: 10
};

const slice = createSlice({
    name: 'overview',
    initialState,
    reducers: {
        hasError(state, action) {
            state.error = action.payload;
        },

        isLoading(state, action) {
            state.loading = action.payload;
        },

        isStatsLoading(state, action) {
            state.statsLoading = action.payload;
        },

        isSingleCallDataLoading(state, action) {
            state.singleCallDataLoading = action.payload;
        },

        setStatsSuccess(state, action) {
            state.stats = action.payload;
        },

        setAllCallHistorySuccess(state, action) {
            state.allCallHistory = action?.payload?.data;
            state.totalPages = action?.payload?.totalPages;
            state.totalCount = action?.payload?.totalCount;
            state.resSummary = action?.payload?.resSummary;
        },

        setSingleCallDataSuccess(state, action) {
            state.singleCallData = action.payload;
        },

        // Pre-populate filters from localStorage if present
        prePopulateFilters(state) {
            const storedFilters = new StorageManager('overview_filter_query', 'OTHERS', false).getStorage();
            if (storedFilters) {
                state.filters = { ...state.filters, ...storedFilters };
            }
        },

        setFilter(state, action) {
            const { name, value } = action.payload;
            if (state.filters[name]) {
                state.filters[name].value = value;
            }
            new StorageManager('overview_filter_query', 'OTHERS', false, state.filters).setStorage();
        },

        setFilterDisabled(state, action) {
            const { name, disabled } = action.payload;
            if (state.filters[name]) {
                state.filters[name].disabled = disabled;
            }
        },

        clearFilter(state, action) {
            const { name } = action.payload;
            if (state.filters[name]) {
                state.filters[name].value = initialState.filters[name].value;
            }
        },

        clearAllFilters(state) {
            state.filters = { ...initialState.filters };
        },

        changeCurrentPage(state, action) {
            state.currentPage = action.payload;
        },

        // Stats filters actions
        setStatsFilter(state, action) {
            const { name, value } = action.payload;
            if (state.statsFilters[name]) {
                state.statsFilters[name].value = value;
            }
        },

        clearStatsFilters(state) {
            state.statsFilters = { ...initialState.statsFilters };
        },
    },
});

// Reducer
export default slice.reducer;

// Filter Actions
export const {
    setFilter,
    clearFilter,
    clearAllFilters,
    changeCurrentPage,
    setFilterDisabled,
    prePopulateFilters,
    setStatsFilter,
    clearStatsFilters
} = slice.actions;

// Selectors
export const selectFilters = (state) => state.overview.filters;
export const selectStatsFilters = (state) => state.overview.statsFilters;
export const selectStats = (state) => state.overview.stats;

// API Calls

// Get overview statistics with optional date filters
export function getOverviewStats({ start_date = '', end_date = '' } = {}) {
    return async () => {
        try {
            dispatch(slice.actions.isStatsLoading(true));

            const queryParams = new URLSearchParams();
            if (start_date) queryParams.append('start_date', start_date);
            if (end_date) queryParams.append('end_date', end_date);

            const queryString = queryParams.toString();
            const url = `/call-history/overview-stats${queryString ? `?${queryString}` : ''}`;

            const response = await axios.get(url);
            dispatch(slice.actions.setStatsSuccess(response.data?.data || response.data));

            return response.data?.data || response.data;
        }
        catch (err) {
            handleError(err, dispatch);
        }
        finally {
            dispatch(slice.actions.isStatsLoading(false));
        }
    };
}

// Get all call history for overview (with full details)
export function getOverviewCallHistory({ page, limit }) {
    return async (_, getState) => {
        try {
            dispatch(slice.actions.isLoading(true));

            // Extract filters from state
            const { filters } = getState().overview;
            const { search, start_date, end_date, call_type, call_order } = filters;

            const response = await axios.get(
                `/call-history/overview?search=${search?.value}&start_date=${start_date?.value}&end_date=${end_date?.value}&call_type=${call_type?.value}&call_order=${call_order?.value}&page=${page}&limit=${limit}`
            );
            dispatch(slice.actions.setAllCallHistorySuccess(response.data));

            return response.data?.data;
        }
        catch (err) {
            handleError(err, dispatch);
        }
        finally {
            dispatch(slice.actions.isLoading(false));
        }
    };
}

// Get single call data
export function getOverviewSingleCallData(callHistoryId, callType = 'inbound') {
    return async () => {
        try {
            dispatch(slice.actions.isSingleCallDataLoading(true));

            const endpoint = callType === 'outbound'
                ? `/call-history/get-outbound-history/${callHistoryId}`
                : `/call-history/get/${callHistoryId}`;

            const response = await axios.get(endpoint);
            dispatch(slice.actions.setSingleCallDataSuccess(response.data?.data));

            return response.data?.data;
        }
        catch (err) {
            handleError(err, dispatch);
        }
        finally {
            setTimeout(async () => {
                dispatch(slice.actions.isSingleCallDataLoading(false));
            }, 800);
        }
    };
}

// Export CSV for overview
export function exportOverviewCSV() {
    return async (_, getState) => {
        try {
            const { filters } = getState().overview;

            const queryParams = new URLSearchParams({
                search: filters?.search?.value || '',
                start_date: filters?.start_date?.value || '',
                end_date: filters?.end_date?.value || '',
                call_type: filters?.call_type?.value || '',
                call_order: filters?.call_order?.value || 'desc'
            }).toString();

            const response = await axios({
                url: `call-history/export-csv?${queryParams}`,
                method: 'GET',
                responseType: 'blob',
            });

            return response.data;
        }
        catch (err) {
            handleError(err, dispatch);
            throw err;
        }
    };
}
